import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { config } from '../config';
import { activeOrganizationId, graphql } from './client';

/** Loaded once, and only when a key exists — development uses no gateway at all. */
let stripePromise: Promise<Stripe | null> | null = null;
export function stripeClient(): Promise<Stripe | null> | null {
  if (!config.stripePublishableKey) return null;
  stripePromise ??= loadStripe(config.stripePublishableKey);
  return stripePromise;
}

/**
 * Collects one charge now. If the bank asks the cardholder to confirm, the
 * browser confirms with the processor and the server then retrieves the same
 * payment — browser success is not accounting evidence. Returns the charge's
 * resulting status; a decline or failed confirmation throws.
 */
export async function payCharge(token: string, chargeId: string): Promise<string> {
  const collect = () => graphql<{ payChargeNow: { status: string; clientSecret: string | null; lastFailureMessage: string | null } }>({
    query: 'mutation P($id:ID!){payChargeNow(chargeId:$id){id status clientSecret lastFailureMessage}}',
    variables: { id: chargeId }, token, organizationId: activeOrganizationId() });
  let result = (await collect()).payChargeNow;
  if (result.clientSecret && result.status === 'REQUIRES_ACTION') result = await confirmThenCollect(result.clientSecret, collect);
  if (result.status === 'FAILED' || result.status === 'ESCALATED') throw new Error(result.lastFailureMessage ?? 'The payment did not go through.');
  return result.status;
}

/** Confirms a payment the bank flagged, then re-collects on the server. */
export async function confirmThenCollect<T>(clientSecret: string, collect: () => Promise<{ payChargeNow: T }>): Promise<T> {
  const stripe = await stripeClient();
  if (!stripe) throw new Error('Payment authentication is unavailable. Please contact support.');
  const confirmed = await stripe.confirmCardPayment(clientSecret);
  if (confirmed.error) throw new Error(confirmed.error.message ?? 'Payment was not confirmed.');
  return (await collect()).payChargeNow;
}
